import { Component, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { auditTime } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import Highcharts from 'highcharts';
import { CoInvestmentDetails } from 'src/app/models/classes/co-investment-details.class';
import { DeviceType } from 'src/app/models/enums/breakpoint.enum';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { CurrencySymbolPipe } from 'src/app/pipes/currency-symbol.pipe';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { CYAN, GROSVENOR_BLUE, MIST, NAVY, WHITE } from '@gcm/gcm-ui-angular';
import { BaseWidgetComponent } from 'src/app/shared/base-widget/base-widget.component';
import { GcmChartComponent } from 'src/app/shared/gcm-chart/gcm-chart.component';
import { CaseStudyWidget } from 'src/app/models/enums/case-study-widget.enum';
import { KeyValuePair } from 'src/app/models/classes/key-value-pair.class';

interface FinancialPeriodData {
  period: string;
  valuationDriver: number | null;
  topLine: number | null;
  margin: number | null;
}

@Component({
  selector: 'app-financial-performance-widget',
  imports: [BaseWidgetComponent, GcmChartComponent, MatIconModule, CurrencySymbolPipe],
  templateUrl: './financial-performance-widget.component.html'
})
export class FinancialPerformanceWidgetComponent {
  private breakpointService = inject(BreakpointService);
  private currencyFormat = inject(CurrencyFormatPipe);

  performanceChartStyle = { width: '100%', display: 'flex' };
  performanceChartOptions = {};
  linelegendsymbol: ((legend: Highcharts.Legend, item: Highcharts.Series | Highcharts.Point) => void) | null = null;
  error = "";
  reflowEvent: unknown;
  readonly investmentSummary = input<CoInvestmentDetails>();
  performanceChartData!: FinancialPeriodData[];
  readonly widgets = input<Record<CaseStudyWidget, KeyValuePair<string | number, number>[]>>();
  private readonly deviceType = toSignal(
    this.breakpointService.getDeviceType().pipe(auditTime(50)),
    { initialValue: DeviceType.Web }
  );

  private readonly rebuildChart = effect(() => {
    if (this.widgets()) {
      this.createPerformanceChart();
    }
  });

  createPerformanceChart() {
    if (this.investmentSummary()?.confidential) {
      this.error = "Confidential"
      return;
    }
    const widgets = this.widgets() as Record<CaseStudyWidget, KeyValuePair<string | number, number>[]>;
    this.performanceChartData = this.combineFinancialData(widgets.valuationDriverByPeriod, widgets.topLineByPeriod, widgets.marginByPeriod);
    //Creating Performance widget with Highcharts
    const seriesTypes = (Highcharts as unknown as { seriesTypes: Record<string, { prototype: Record<string, unknown> }> }).seriesTypes;
    this.linelegendsymbol = seriesTypes['line'].prototype['drawLegendSymbol'] as typeof this.linelegendsymbol;
    seriesTypes['line'].prototype['drawLegendSymbol'] = seriesTypes['area'].prototype['drawLegendSymbol'];
    const currencyFormat = this.currencyFormat;
    const investmentSummary = this.investmentSummary() as CoInvestmentDetails;
    const isMobileDevice = this.deviceType() === DeviceType.Handset;
    {
      this.performanceChartOptions = {
        chart: {
          height: 400
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: this.performanceChartData.map(x => x.period),
          labels: {
            staggerLines: isMobileDevice ? 2 : 0,
            style: {
              fontSize: isMobileDevice ? '10px' : '12px'
            }
          }
        },
        yAxis: [{
          title: {
            text: ''
          },
          labels: {
            enabled: true,
            reserveSpace: true,
            formatter: function (context: Highcharts.AxisLabelsFormatterContextObject) {
              //show in millions, if less than 1 million then show 1 decimal point
              const fractionDigits = Math.abs(Number(context.value)) >= 1000000 ? 0 : 1
              const valToShow = currencyFormat.transform(Number(context.value), investmentSummary.currency, 'Millions', false, false, fractionDigits, true);
              return valToShow;
            },
            style: {
              fontSize: isMobileDevice ? '10px' : '12px'
            }
          },
          gridLineDashStyle: 'Dash',
          gridLineWidth: 1,
          gridLineColor: MIST
        }, {
          visible: false,
          min: Math.min(0, ...this.performanceChartData.map(x => x.margin).filter(m => m != null)),
          max: 1,
          height: '15%'
        }],
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'top',
          itemStyle: {
            cursor: 'default',
            fontWeight: 'light',
            fontSize: isMobileDevice ? '11px' : '13px'
          },
          itemHoverStyle: {
            transition: 'none'
          },
          symbolHeight: 12,
          symbolWidth: 12,
          x: -18
        },
        tooltip: {
          backgroundColor: NAVY,
          borderColor: WHITE,
          borderRadius: 8,
          style: {
            color: WHITE,
            fontSize: isMobileDevice ? '11px' : '14px'
          },
          useHTML: true,
          formatter: function (this: Highcharts.Point) {
            const valToShow = this.series.name.includes('%') ? `${(Number(this.y) * 100).toFixed(1)}%` : currencyFormat.transform(Number(this.y), investmentSummary.currency, 'Millions', false, false, 1, true);
            const data = `${this.series.name} <br/>` + valToShow + ` <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${WHITE}">`
            const footer = `${this.x}`
            return data + footer
          },
          borderWidth: 0.5,
          shadow: true
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: false
            }
          },
          column: {
            grouping: true,
            groupPadding: 0.2,
            pointPadding: 0,
            dataLabels: {
              enabled: false
            },
            states: {
              inactive: {
                opacity: 1
              }
            },
            events: {
              legendItemClick: function () {
                return false;
              }
            }
          }
        },
        series: [{
          name: this.investmentSummary()?.topLineLabel,
          data: this.performanceChartData.map(x => x.topLine),
          type: 'column',
          color: GROSVENOR_BLUE,
          states: {
            hover: {
              color: GROSVENOR_BLUE
            }
          }
        }, {
          name: this.investmentSummary()?.valuationDriverLabel,
          data: this.performanceChartData.map(x => x.valuationDriver),
          type: 'column',
          color: NAVY,
          states: {
            hover: {
              color: NAVY
            }
          }
        }, {
          name: '% Margin',
          data: this.performanceChartData.map(x => x.margin),
          type: 'line',
          yAxis: 1,
          color: CYAN,
          borderColor: CYAN,
          states: {
            hover: {
              lineWidth: 2,
            },
            inactive: {
              opacity: 1
            }
          },
          legend: {
            bubbleLegend: {
              borderColor: CYAN,
              connectorColor: WHITE
            }
          },
          events: {
            legendItemClick: function () {
              return false;
            }
          }
        }]
      }
    }
  }

  combineFinancialData(valuationDriverByPeriod: KeyValuePair<string | number, number>[], topLineByPeriod: KeyValuePair<string | number, number>[], marginByPeriod: KeyValuePair<string | number, number>[]): FinancialPeriodData[] {
    const valuationDriverMap = new Map(valuationDriverByPeriod?.map(x => [String(x.key), x.value]) ?? []);
    const topLineMap = new Map(topLineByPeriod?.map(x => [String(x.key), x.value]) ?? []);
    const marginMap = new Map(marginByPeriod?.map(x => [String(x.key), x.value]) ?? []);

    // Get all unique periods from all data sources
    const allPeriods = new Set([
      ...valuationDriverMap.keys(),
      ...topLineMap.keys(),
      ...marginMap.keys()
    ]);

    return Array.from(allPeriods)
      .sort()
      .map(period => ({
        period,
        valuationDriver: valuationDriverMap.get(period) ?? null,
        topLine: topLineMap.get(period) ?? null,
        margin: marginMap.get(period) ?? null
      }));
  }
}
